/**
 * ClientMessageModal — Messaging UI from a client's perspective.
 * Shows conversation with their coach, loads from coach_messages table.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, Modal, TouchableOpacity,
  TextInput, FlatList, ActivityIndicator, KeyboardAvoidingView, Platform,
} from 'react-native';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { X, Send } from 'lucide-react-native';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface Message {
  id: string;
  coach_id: string;
  recipient_ids: string[];
  content: string;
  sent_at: string;
}

interface Props {
  visible: boolean;
  onClose: () => void;
}

export default function ClientMessageModal({ visible, onClose }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;

  const [messages, setMessages] = useState<Message[]>([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [myId, setMyId] = useState('');
  const [coachId, setCoachId] = useState('');
  const [coachName, setCoachName] = useState('Your Coach');
  const flatListRef = useRef<FlatList>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);

  const loadConversation = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const userId = user.id;
      setMyId(userId);

      // Find my coach via coach_athlete_links
      const { data: links } = await supabase
        .from('coach_athlete_links')
        .select('coach_user_id')
        .eq('athlete_user_id', userId)
        .eq('status', 'active')
        .limit(1);

      if (!links || links.length === 0) {
        setCoachName('No Coach');
        setMessages([]);
        return;
      }

      const foundCoachId = links[0].coach_user_id;
      setCoachId(foundCoachId);

      // Get coach display name
      const { data: coachProfile } = await supabase
        .from('profiles')
        .select('display_name, handle')
        .eq('id', foundCoachId)
        .maybeSingle();

      if (coachProfile) {
        setCoachName(coachProfile.display_name || coachProfile.handle || 'Coach');
      }

      // Load messages between me and my coach (DMs: single-recipient messages)
      const { data: msgs, error } = await supabase
        .from('coach_messages')
        .select('id, coach_id, recipient_ids, content, sent_at')
        .or(`coach_id.eq.${foundCoachId},coach_id.eq.${userId}`)
        .order('sent_at', { ascending: true })
        .limit(200);

      if (!error && msgs) {
        const filtered = msgs.filter((m: any) => {
          const rIds: string[] = m.recipient_ids ?? [];
          // Coach sent to me
          if (m.coach_id === foundCoachId && rIds.includes(userId)) return true;
          // I sent to coach
          if (m.coach_id === userId && rIds.includes(foundCoachId)) return true;
          return false;
        });
        setMessages(filtered);
      }
    } catch (err) {
      console.warn('[ClientMessageModal] load error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    setText('');
    loadConversation();

    // Real-time subscription for incoming messages
    const channel = supabase
      .channel('client-dm')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'coach_messages' },
        (payload) => {
          const msg = payload.new as Message;
          const rIds = msg.recipient_ids ?? [];
          // Message from my coach to me, or from me to coach
          if (
            (msg.coach_id === coachId && rIds.includes(myId)) ||
            (msg.coach_id === myId && rIds.includes(coachId))
          ) {
            setMessages((prev) => {
              if (prev.some((m) => m.id === msg.id)) return prev;
              return [...prev, msg];
            });
            setTimeout(() => flatListRef.current?.scrollToEnd({ animated: true }), 100);
          }
        },
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [visible, loadConversation, coachId, myId]);

  const handleSend = async () => {
    if (!text.trim() || sending || !coachId) return;
    setSending(true);
    try {
      const msg: Message = {
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
        coach_id: myId, // client sends as "coach_id" (sender field)
        content: text.trim(),
        recipient_ids: [coachId],
        sent_at: new Date().toISOString(),
      };
      const { error } = await supabase.from('coach_messages').insert(msg);
      if (error) console.warn('[ClientMessageModal] send error', error.message);
      setText('');
      setMessages((prev) => [...prev, msg]);
      setTimeout(() => flatListRef.current?.scrollToEnd({ animated: true }), 100);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });

  const renderMessage = ({ item }: { item: Message }) => {
    const isMe = item.coach_id === myId;
    return (
      <View style={[styles.bubble, isMe ? styles.bubbleRight : styles.bubbleLeft,
        { backgroundColor: isMe ? c.accent : c.surface }]}>
        <Text style={[styles.bubbleText, { color: isMe ? '#fff' : c.text }]}>
          {item.content}
        </Text>
        <Text style={[styles.timestamp, { color: isMe ? 'rgba(255,255,255,0.7)' : c.textTertiary }]}>
          {formatDate(item.sent_at)} {formatTime(item.sent_at)}
        </Text>
      </View>
    );
  };

  return (
    <Modal visible={visible} animationType="slide" transparent>
      <KeyboardAvoidingView
        style={styles.overlay}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={[styles.modal, { backgroundColor: c.background, borderColor: c.border }]}>
          {/* Header */}
          <View style={styles.header}>
            <View>
              <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>
                {coachName}
              </Text>
              <Text style={[styles.subtitle, { color: c.textSecondary }]}>Messages</Text>
            </View>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <X size={22} color={c.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Messages */}
          {loading ? (
            <View style={styles.loadingWrap}>
              <ActivityIndicator color={c.accent} />
            </View>
          ) : !coachId ? (
            <View style={styles.loadingWrap}>
              <Text style={[styles.emptyText, { color: c.textTertiary }]}>
                You don't have a coach yet. Ask a coach to send you an invite!
              </Text>
            </View>
          ) : (
            <FlatList
              ref={flatListRef}
              data={messages}
              keyExtractor={(m) => m.id}
              renderItem={renderMessage}
              contentContainerStyle={styles.messageList}
              keyboardShouldPersistTaps="handled"
              onContentSizeChange={() => flatListRef.current?.scrollToEnd({ animated: false })}
              ListEmptyComponent={
                <Text style={[styles.emptyText, { color: c.textTertiary }]}>
                  No messages yet. Say hello to your coach!
                </Text>
              }
            />
          )}

          {/* Input */}
          {coachId ? (
            <View style={[styles.inputRow, { borderColor: c.border }]}>
              <TextInput
                style={[styles.input, { color: c.text, backgroundColor: c.surface, borderColor: c.border }]}
                placeholder="Type a message..."
                placeholderTextColor={c.textTertiary}
                value={text}
                onChangeText={setText}
                multiline
                maxLength={500}
              />
              <TouchableOpacity
                style={[styles.sendBtn, { backgroundColor: text.trim() ? c.accent : c.border }]}
                onPress={handleSend}
                disabled={sending || !text.trim()}
              >
                {sending ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Send size={18} color="#fff" />
                )}
              </TouchableOpacity>
            </View>
          ) : null}
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'flex-end',
  },
  modal: {
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    borderWidth: 1,
    borderBottomWidth: 0,
    maxHeight: '90%',
    minHeight: '60%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    paddingBottom: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: '900',
    letterSpacing: 0.5,
  },
  subtitle: {
    fontSize: 12,
    fontWeight: '500',
    marginTop: 1,
  },
  loadingWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  messageList: {
    padding: 16,
    paddingTop: 4,
    flexGrow: 1,
  },
  bubble: {
    maxWidth: '78%',
    borderRadius: 2,
    padding: 10,
    paddingHorizontal: 14,
    marginBottom: 8,
  },
  bubbleLeft: {
    alignSelf: 'flex-start',
    borderBottomLeftRadius: 4,
  },
  bubbleRight: {
    alignSelf: 'flex-end',
    borderBottomRightRadius: 4,
  },
  bubbleText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
  },
  timestamp: {
    fontSize: 11,
    fontWeight: '500',
    marginTop: 4,
    textAlign: 'right',
  },
  emptyText: {
    textAlign: 'center',
    fontSize: 14,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    padding: 12,
    gap: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  input: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 0,
    padding: 10,
    fontSize: 14,
    maxHeight: 100,
    textAlignVertical: 'top',
  },
  sendBtn: {
    width: 40,
    height: 40,
    borderRadius: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
